import React, { useEffect, useMemo } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SchemaRoundedIcon from "@mui/icons-material/SchemaRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { getAuthToken } from "../utils/session";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const COLORS = {
  page: "#F7F9FC",
  pageSoft: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceSoft: "#FBFCFE",
  surfaceStrong: "#FFFFFF",

  border: "rgba(15,36,56,0.10)",
  borderSoft: "rgba(15,36,56,0.07)",
  borderStrong: "rgba(15,36,56,0.16)",

  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",
  mutedSoft: "#94A3B8",

  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",

  accent: "#D37D36",
  accentDark: "#B6632C",
  accentSoft: "#FFF4EA",

  success: "#14A67A",
  successSoft: "#EAFBF6",

  purple: "#5B5CE2",
  purpleSoft: "#F0F0FF",

  warning: "#C47A1F",
  warningSoft: "#FFF7E8",

  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",

  shadow: "0 18px 48px rgba(15,36,56,0.06)",
  hoverShadow: "0 18px 42px rgba(15,36,56,0.08)",

  background:
    "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
};

const TYPO = {
  pageTitle: 900,
  sectionTitle: 820,
  itemTitle: 760,
  bodyStrong: 680,
  body: 520,
  meta: 650,
  chip: 800,
  button: 820,
};

const cardSx = {
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "22px",
  boxShadow: COLORS.shadow,
};

const softCardSx = {
  bgcolor: COLORS.surfaceSoft,
  border: `1px solid ${COLORS.borderSoft}`,
  borderRadius: "18px",
};

const getUserRole = () => {
  try {
    const token = getAuthToken();
    if (!token) return "ROLE_FREEMIUM";

    return jwtDecode(token).role || "ROLE_FREEMIUM";
  } catch (error) {
    console.error("Error al decodificar el token:", error);
    return "ROLE_FREEMIUM";
  }
};

const getPlanInfo = (role) => {
  if (role === "ROLE_PREMIUMPRO" || role === "ROLE_PREMIUM_PRO") {
    return {
      label: "Premium Pro",
      maturityLevel: "Madurez organizacional hasta nivel 5",
      maturityImage: "/images/nivelesMadurez3.jpg",
      generalQuestions: "Niveles 2, 3, 4 y 5",
      scope: "Evaluación avanzada de capacidad y madurez organizacional",
      color: COLORS.accent,
      bg: COLORS.accentSoft,
    };
  }

  if (role === "ROLE_PREMIUM") {
    return {
      label: "Premium",
      maturityLevel: "Madurez organizacional hasta nivel 2",
      maturityImage: "/images/nivelesMadurez2.jpg",
      generalQuestions: "Nivel 2",
      scope: "Evaluación ampliada con preguntas transversales de gestión",
      color: COLORS.purple,
      bg: COLORS.purpleSoft,
    };
  }

  return {
    label: "Freemium",
    maturityLevel: "Capacidad de procesos",
    maturityImage: null,
    generalQuestions: null,
    scope: "Evaluación básica de procesos seleccionados",
    color: COLORS.primary,
    bg: COLORS.primarySoft,
  };
};

const InstruccionesPage = () => {
  const navigate = useNavigate();
  const { cuestionarioId } = useParams();

  const userRole = useMemo(() => getUserRole(), []);
  const plan = useMemo(() => getPlanInfo(userRole), [userRole]);

  const hasMaturityView =
    userRole === "ROLE_PREMIUM" ||
    userRole === "ROLE_PREMIUMPRO" ||
    userRole === "ROLE_PREMIUM_PRO";

  useEffect(() => {
    document.body.style.background = COLORS.page;
  }, []);

  const handleStart = () => {
    navigate(`/cuestionario/${cuestionarioId}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        pt: { xs: 9, md: 3 },
        pb: { xs: 5, md: 6 },
        fontFamily: FONT_FAMILY,
        color: COLORS.text,
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1.25, sm: 1.75, md: 2 } }}>
        <TopBar onStart={handleStart} />

        <IntroHeader plan={plan} onStart={handleStart} />

        <Grid container spacing={2.3} sx={{ mt: 0.1 }}>
          <Grid item xs={12} lg={8.2}>
            <Stack spacing={2.2}>
              <GuidedStepsCard />

              <AnswerCriteriaCard />

              <SectionCard
                icon={<SchemaRoundedIcon />}
                title="Estructura del cuestionario"
                subtitle="El cuestionario se organiza por los procesos que has seleccionado. Dentro de cada proceso responderás preguntas concretas sobre cómo se trabaja actualmente en tu organización."
              >
                <Grid container spacing={1.3} sx={{ mt: 0.6 }}>
                  <Grid item xs={12} sm={6}>
                    <InfoTile
                      title="Bloques por proceso"
                      text="Cada proceso tiene sus propias preguntas y su propio avance. Puedes moverte entre procesos sin perder lo respondido."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoTile
                      title="Una respuesta por pregunta"
                      text="Selecciona la opción que mejor represente la situación real. No busques la respuesta ideal, sino la más fiel a la práctica actual."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoTile
                      title="Preguntas con ayuda"
                      text="Algunas preguntas incluyen una explicación adicional. Úsala para aclarar conceptos o interpretar correctamente el alcance."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoTile
                      title="Resultados posteriores"
                      text="Al finalizar, se generarán resultados de capacidad, madurez cuando aplique y propuestas de mejora según tu plan."
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard
                icon={<ShieldRoundedIcon />}
                title="Marco de referencia"
                subtitle="La evaluación se apoya en las normas UNE relacionadas con gobierno, gestión, calidad y madurez del dato, pero está redactada para que pueda responderse sin conocimientos normativos avanzados."
              >
                <Grid container spacing={1.3} sx={{ mt: 0.6 }}>
                  {[
                    { norm: "UNE 0077", label: "Gobierno del dato" },
                    { norm: "UNE 0078", label: "Gestión del dato" },
                    { norm: "UNE 0079", label: "Calidad del dato" },
                    { norm: "UNE 0080", label: "Evaluación de madurez" },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.norm}>
                      <NormTile norm={item.norm} label={item.label} />
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>

              {hasMaturityView && (
                <SectionCard
                  icon={<TrendingUpRoundedIcon />}
                  title={plan.maturityLevel}
                  subtitle="Este esquema ayuda a entender cómo se relacionan la capacidad de los procesos con la madurez organizacional según el alcance disponible en tu plan."
                >
                  <Box
                    sx={{
                      mt: 1.2,
                      p: { xs: 1, md: 1.25 },
                      borderRadius: "20px",
                      bgcolor: COLORS.surfaceSoft,
                      border: `1px solid ${COLORS.border}`,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={plan.maturityImage}
                      alt="Nivel de madurez"
                      sx={{
                        width: "100%",
                        maxWidth: 720,
                        borderRadius: "14px",
                        display: "block",
                      }}
                    />
                  </Box>
                </SectionCard>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={3.8}>
            <Stack spacing={2.2} sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
              <QuickGuideCard onStart={handleStart} plan={plan} />

              <KeyFeaturesCard />

              <PracticalTipsCard />

              {userRole !== "ROLE_FREEMIUM" && (
                <GeneralQuestionsCard plan={plan} />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const TopBar = ({ onStart }) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSx,
      borderRadius: "20px",
      px: { xs: 1.45, md: 1.9 },
      py: 1.15,
      mb: 2,
      bgcolor: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(18px)",
      position: "sticky",
      top: { xs: 72, md: 12 },
      zIndex: 20,
    }}
  >
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.2}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "center" }}
    >
      <Breadcrumbs
        separator={<NavigateNextRoundedIcon fontSize="small" sx={{ color: COLORS.muted }} />}
        aria-label="breadcrumb"
      >
        <MuiLink
          component={RouterLink}
          to="/mis-autoevaluaciones"
          underline="hover"
          sx={{
            display: "flex",
            alignItems: "center",
            color: COLORS.primary,
            fontWeight: TYPO.meta,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          <HomeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
          Dashboard
        </MuiLink>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.meta,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          Instrucciones
        </Typography>
      </Breadcrumbs>

      <Button
        variant="contained"
        startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={onStart}
        sx={primaryButtonSx}
      >
        Comenzar cuestionario
      </Button>
    </Stack>
  </Paper>
);

const IntroHeader = ({ plan, onStart }) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSx,
      mb: 2.4,
      p: { xs: 2.1, md: 2.8 },
      borderRadius: "26px",
      position: "relative",
      overflow: "hidden",
      background:
        "radial-gradient(circle at top right, rgba(211,125,54,0.13), transparent 32%), linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)",
    }}
  >
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ maxWidth: 840 }}>
        <Stack direction="row" spacing={0.9} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.1 }}>
          <Chip
            label="Guía previa"
            size="small"
            sx={{
              height: 24,
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              border: `1px solid ${COLORS.primary}22`,
              fontFamily: FONT_FAMILY,
            }}
          />

          <Chip
            label={`Plan ${plan.label}`}
            size="small"
            sx={{
              height: 24,
              bgcolor: plan.bg,
              color: plan.color,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              border: `1px solid ${plan.color}22`,
              fontFamily: FONT_FAMILY,
            }}
          />
        </Stack>

        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: { xs: "1.48rem", md: "2rem" },
            letterSpacing: "-0.055em",
            lineHeight: 1.08,
            mb: 0.9,
            fontFamily: FONT_FAMILY,
          }}
        >
          Instrucciones para completar la autoevaluación
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: { xs: "0.82rem", md: "0.9rem" },
            lineHeight: 1.65,
            maxWidth: 820,
            fontFamily: FONT_FAMILY,
          }}
        >
          Esta guía te ayudará a responder con coherencia. El objetivo no es demostrar que todo está implantado, sino obtener un diagnóstico realista para priorizar mejoras.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.6 }}>
          <HeroPill icon={<AccessTimeRoundedIcon />} label="Puedes continuar más tarde" />
          <HeroPill icon={<SaveRoundedIcon />} label="Guardado automático" />
          <HeroPill icon={<FactCheckRoundedIcon />} label="Responde con evidencias" />
        </Stack>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: 300 },
          p: 1.4,
          borderRadius: "22px",
          bgcolor: "rgba(255,255,255,0.72)",
          border: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.sectionTitle,
            fontSize: "0.86rem",
            mb: 0.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          Alcance de tu evaluación
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: "0.76rem",
            lineHeight: 1.5,
            mb: 1.2,
            fontFamily: FONT_FAMILY,
          }}
        >
          {plan.scope}
        </Typography>

        <Button
          variant="contained"
          startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={onStart}
          sx={primaryButtonSx}
          fullWidth
        >
          Ir al cuestionario
        </Button>
      </Box>
    </Stack>
  </Paper>
);

const GuidedStepsCard = () => (
  <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.9, md: 2.3 } }}>
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.7 }}>
      <Avatar
        sx={{
          bgcolor: COLORS.primarySoft,
          color: COLORS.primary,
          width: 40,
          height: 40,
          border: `1px solid ${COLORS.primary}22`,
        }}
      >
        <RouteRoundedIcon />
      </Avatar>

      <Box>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "1rem",
            letterSpacing: "-0.025em",
            fontFamily: FONT_FAMILY,
          }}
        >
          Cómo responder el cuestionario
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: "0.76rem",
            lineHeight: 1.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          Sigue esta secuencia para que el resultado sea homogéneo y útil.
        </Typography>
      </Box>
    </Stack>

    <Grid container spacing={1.3}>
      <Grid item xs={12} md={6}>
        <StepTile
          number="1"
          title="Lee la pregunta completa"
          text="Identifica qué práctica, control, responsabilidad, evidencia o forma de trabajo se está evaluando."
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <StepTile
          number="2"
          title="Responde según la realidad actual"
          text="No respondas pensando en lo que debería existir. Selecciona la opción que mejor refleje cómo se trabaja hoy."
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <StepTile
          number="3"
          title="Piensa en evidencias"
          text="Ten en cuenta documentos, procedimientos, sistemas, roles definidos, controles, métricas o prácticas aplicadas."
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <StepTile
          number="4"
          title="Usa la ayuda si tienes dudas"
          text="Cuando una pregunta incluya ayuda, revísala para interpretar correctamente el alcance antes de responder."
        />
      </Grid>
    </Grid>

    <Alert
      severity="info"
      sx={{
        mt: 1.8,
        borderRadius: "16px",
        bgcolor: COLORS.primarySoft,
        border: `1px solid ${COLORS.primary}22`,
        color: COLORS.text,
        fontSize: "0.78rem",
        fontFamily: FONT_FAMILY,

        "& .MuiAlert-icon": {
          color: COLORS.primary,
        },
      }}
    >
      Puedes guardar el avance y continuar más tarde. No es necesario completar todo el cuestionario en una única sesión.
    </Alert>
  </Paper>
);

const AnswerCriteriaCard = () => (
  <SectionCard
    icon={<AssignmentTurnedInRoundedIcon />}
    title="Criterios recomendados para elegir una respuesta"
    subtitle="Para que el diagnóstico sea útil, conviene aplicar criterios comunes al interpretar cada pregunta."
  >
    <Grid container spacing={1.3} sx={{ mt: 0.6 }}>
      <Grid item xs={12} md={4}>
        <CriteriaTile
          title="Sí"
          text="Selecciona esta opción si la práctica existe, está definida y se aplica de forma habitual."
          color={COLORS.success}
          bg={COLORS.successSoft}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CriteriaTile
          title="Parcialmente"
          text="Úsala si existe alguna práctica, pero no está formalizada, no se aplica siempre o depende de personas concretas."
          color={COLORS.warning}
          bg={COLORS.warningSoft}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CriteriaTile
          title="No"
          text="Márcala si no existe una práctica clara, no hay evidencia suficiente o todavía no se ha implantado."
          color={COLORS.danger}
          bg={COLORS.dangerSoft}
        />
      </Grid>
    </Grid>
  </SectionCard>
);

const QuickGuideCard = ({ onStart, plan }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.1 }}>
    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.pageTitle,
        fontSize: "1rem",
        letterSpacing: "-0.025em",
        mb: 0.45,
        fontFamily: FONT_FAMILY,
      }}
    >
      Antes de empezar
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontWeight: TYPO.body,
        fontSize: "0.76rem",
        lineHeight: 1.55,
        mb: 1.6,
        fontFamily: FONT_FAMILY,
      }}
    >
      Ten presentes estas reglas para obtener un diagnóstico consistente.
    </Typography>

    <Stack spacing={1.15} sx={{ mb: 1.7 }}>
      <QuickPoint
        number="1"
        title="Sé realista"
        text="El objetivo es conocer el punto de partida."
      />

      <QuickPoint
        number="2"
        title="No necesitas ser experto"
        text="Las preguntas están redactadas para distintos perfiles."
      />

      <QuickPoint
        number="3"
        title="Puedes retomar después"
        text="El avance queda guardado durante la evaluación."
      />
    </Stack>

    <Box
      sx={{
        p: 1.35,
        borderRadius: "18px",
        bgcolor: plan.bg,
        border: `1px solid ${plan.color}22`,
        mb: 1.5,
      }}
    >
      <Typography
        sx={{
          color: plan.color,
          fontWeight: TYPO.pageTitle,
          fontSize: "0.78rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Plan {plan.label}
      </Typography>

      <Typography
        sx={{
          color: COLORS.text,
          fontWeight: TYPO.body,
          fontSize: "0.74rem",
          mt: 0.3,
          lineHeight: 1.45,
          fontFamily: FONT_FAMILY,
        }}
      >
        El cuestionario se adapta al alcance disponible en tu plan.
      </Typography>
    </Box>

    <Button
      variant="contained"
      startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
      onClick={onStart}
      sx={primaryButtonSx}
      fullWidth
    >
      Ir al cuestionario
    </Button>
  </Paper>
);

const KeyFeaturesCard = () => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.1 }}>
    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.pageTitle,
        fontSize: "1rem",
        mb: 1.4,
        fontFamily: FONT_FAMILY,
      }}
    >
      Funcionalidades útiles
    </Typography>

    <Stack spacing={1.1}>
      <FeatureRow
        icon={<SaveRoundedIcon />}
        title="Guardado automático"
        text="Evita pérdida de progreso."
        color={COLORS.success}
        bg={COLORS.successSoft}
      />

      <FeatureRow
        icon={<RestartAltRoundedIcon />}
        title="Retomar sesión"
        text="Continúa desde donde lo dejaste."
        color={COLORS.primary}
        bg={COLORS.primarySoft}
      />

      <FeatureRow
        icon={<RouteRoundedIcon />}
        title="Navegación por procesos"
        text="Responde por bloques de forma ordenada."
        color={COLORS.purple}
        bg={COLORS.purpleSoft}
      />

      <FeatureRow
        icon={<HelpOutlineRoundedIcon />}
        title="Ayuda contextual"
        text="Consulta ejemplos si tienes dudas."
        color={COLORS.accent}
        bg={COLORS.accentSoft}
      />
    </Stack>
  </Paper>
);

const PracticalTipsCard = () => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.1 }}>
    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
      <Avatar
        sx={{
          width: 38,
          height: 38,
          bgcolor: COLORS.accentSoft,
          color: COLORS.accent,
          border: `1px solid ${COLORS.accent}22`,
        }}
      >
        <TipsAndUpdatesRoundedIcon />
      </Avatar>

      <Box>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "0.95rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          Consejo práctico
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.72rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          Recomendación de uso
        </Typography>
      </Box>
    </Stack>

    <Typography
      sx={{
        color: COLORS.text,
        fontSize: "0.78rem",
        lineHeight: 1.65,
        fontWeight: TYPO.body,
        fontFamily: FONT_FAMILY,
      }}
    >
      Si tienes dudas entre dos respuestas, elige la más conservadora y basada en evidencias. Esto hará que el plan de mejora sea más realista y accionable.
    </Typography>
  </Paper>
);

const GeneralQuestionsCard = ({ plan }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.1 }}>
    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
      <Avatar
        sx={{
          bgcolor: COLORS.warningSoft,
          color: COLORS.warning,
          width: 38,
          height: 38,
          border: `1px solid ${COLORS.warning}22`,
        }}
      >
        <DescriptionRoundedIcon />
      </Avatar>

      <Box>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "0.95rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          Preguntas generales
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontSize: "0.72rem",
            fontWeight: TYPO.meta,
            fontFamily: FONT_FAMILY,
          }}
        >
          {plan.generalQuestions}
        </Typography>
      </Box>
    </Stack>

    <Typography
      sx={{
        color: COLORS.text,
        fontSize: "0.78rem",
        lineHeight: 1.6,
        fontWeight: TYPO.body,
        fontFamily: FONT_FAMILY,
      }}
    >
      Además de las preguntas por proceso, responderás preguntas transversales aplicables al conjunto de procesos seleccionados.
    </Typography>
  </Paper>
);

const SectionCard = ({ icon, title, subtitle, children }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.9, md: 2.3 } }}>
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: COLORS.primarySoft,
          color: COLORS.primary,
          border: `1px solid ${COLORS.primary}22`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.pageTitle,
            fontSize: "1rem",
            letterSpacing: "-0.025em",
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: "0.8rem",
            lineHeight: 1.6,
            mt: 0.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          {subtitle}
        </Typography>

        {children}
      </Box>
    </Stack>
  </Paper>
);

const StepTile = ({ number, title, text }) => (
  <Box
    sx={{
      height: "100%",
      p: 1.45,
      ...softCardSx,
    }}
  >
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      <Box
        sx={{
          width: 31,
          height: 31,
          borderRadius: "12px",
          bgcolor: COLORS.primarySoft,
          color: COLORS.primary,
          display: "grid",
          placeItems: "center",
          fontWeight: TYPO.pageTitle,
          fontSize: "0.78rem",
          border: `1px solid ${COLORS.primary}22`,
          flexShrink: 0,
          fontFamily: FONT_FAMILY,
        }}
      >
        {number}
      </Box>

      <Box>
        <Typography
          sx={{
            color: COLORS.textStrong,
            fontWeight: TYPO.sectionTitle,
            fontSize: "0.84rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: COLORS.muted,
            fontWeight: TYPO.body,
            fontSize: "0.76rem",
            lineHeight: 1.52,
            mt: 0.3,
            fontFamily: FONT_FAMILY,
          }}
        >
          {text}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const InfoTile = ({ title, text }) => (
  <Box
    sx={{
      height: "100%",
      p: 1.4,
      ...softCardSx,
    }}
  >
    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.sectionTitle,
        fontSize: "0.84rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      {title}
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontWeight: TYPO.body,
        fontSize: "0.74rem",
        lineHeight: 1.5,
        mt: 0.35,
        fontFamily: FONT_FAMILY,
      }}
    >
      {text}
    </Typography>
  </Box>
);

const CriteriaTile = ({ title, text, color, bg }) => (
  <Box
    sx={{
      height: "100%",
      p: 1.4,
      borderRadius: "18px",
      bgcolor: bg,
      border: `1px solid ${color}22`,
    }}
  >
    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.45 }}>
      <CheckCircleRoundedIcon sx={{ color, fontSize: 18 }} />

      <Typography
        sx={{
          color,
          fontWeight: TYPO.pageTitle,
          fontSize: "0.82rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>
    </Stack>

    <Typography
      sx={{
        color: COLORS.text,
        fontWeight: TYPO.body,
        fontSize: "0.74rem",
        lineHeight: 1.5,
        fontFamily: FONT_FAMILY,
      }}
    >
      {text}
    </Typography>
  </Box>
);

const NormTile = ({ norm, label }) => (
  <Box
    sx={{
      height: "100%",
      p: 1.2,
      borderRadius: "16px",
      bgcolor: COLORS.primarySoft,
      border: `1px solid ${COLORS.primary}22`,
    }}
  >
    <Typography
      sx={{
        color: COLORS.primary,
        fontWeight: TYPO.pageTitle,
        fontSize: "0.78rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      {norm}
    </Typography>

    <Typography
      sx={{
        color: COLORS.text,
        fontWeight: TYPO.body,
        fontSize: "0.72rem",
        lineHeight: 1.42,
        mt: 0.25,
        fontFamily: FONT_FAMILY,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const FeatureRow = ({ icon, title, text, color, bg }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="flex-start"
    sx={{
      p: 1.15,
      borderRadius: "17px",
      bgcolor: bg,
      border: `1px solid ${color}1F`,
    }}
  >
    <Avatar
      sx={{
        width: 31,
        height: 31,
        bgcolor: COLORS.surface,
        color,
        border: `1px solid ${color}22`,

        "& svg": {
          fontSize: 18,
        },
      }}
    >
      {icon}
    </Avatar>

    <Box>
      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.sectionTitle,
          fontSize: "0.8rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.body,
          fontSize: "0.72rem",
          lineHeight: 1.42,
          mt: 0.15,
          fontFamily: FONT_FAMILY,
        }}
      >
        {text}
      </Typography>
    </Box>
  </Stack>
);

const QuickPoint = ({ number, title, text }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box
      sx={{
        width: 27,
        height: 27,
        borderRadius: "10px",
        bgcolor: COLORS.primarySoft,
        color: COLORS.primary,
        display: "grid",
        placeItems: "center",
        fontWeight: TYPO.pageTitle,
        fontSize: "0.72rem",
        border: `1px solid ${COLORS.primary}22`,
        flexShrink: 0,
        fontFamily: FONT_FAMILY,
      }}
    >
      {number}
    </Box>

    <Box>
      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.sectionTitle,
          fontSize: "0.8rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.body,
          fontSize: "0.72rem",
          lineHeight: 1.45,
          mt: 0.15,
          fontFamily: FONT_FAMILY,
        }}
      >
        {text}
      </Typography>
    </Box>
  </Stack>
);

const HeroPill = ({ icon, label }) => (
  <Chip
    icon={icon}
    label={label}
    size="small"
    sx={{
      height: 26,
      bgcolor: COLORS.surface,
      color: COLORS.text,
      border: `1px solid ${COLORS.border}`,
      fontWeight: TYPO.meta,
      fontSize: "0.68rem",
      fontFamily: FONT_FAMILY,

      "& .MuiChip-icon": {
        color: COLORS.primary,
        fontSize: 16,
      },
    }}
  />
);

const primaryButtonSx = {
  minHeight: 42,
  borderRadius: "14px",
  px: 2.2,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.surface,
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
  boxShadow: "0 14px 32px rgba(30,75,129,0.20)",

  "&:hover": {
    background: `linear-gradient(135deg, ${COLORS.primaryDark}, #0B1F33)`,
    boxShadow: "0 18px 40px rgba(30,75,129,0.26)",
  },
};

export default InstruccionesPage;
